import {
  Repeat,
  Stack,
  TimeIcon,
  Typography,
  TerminateCardIcon,
  Skeleton,
  Avatar,
} from '@razz-team/ui-web';
import { ActivateCardButton } from './ActivateCardButton';
import { ApiCard } from '../../../openapi';
import { isDetailsAvailable } from './CardDetailsButton';
import { CardDetailsButton } from './CardDetailsButton';
import { isFreezable } from './CardFreezeButton';
import { CardFreezeButton } from './CardFreezeButton';
import { isMoreAvailable } from './CardMoreButton';
import { CardMoreButton } from './CardMoreButton';
import { useTranslation } from 'react-i18next';
import { ReactNode } from 'react';

function CardAlert({
  icon,
  title,
  color,
}: {
  icon: React.ReactNode;
  title: ReactNode;
  color: 'purple' | 'info' | 'error';
}) {
  return (
    <Stack direction="row" spacing={2} alignItems="center">
      <Avatar color={color}>{icon}</Avatar>
      <Typography variant="h6">{title}</Typography>
    </Stack>
  );
}

function CardActions({
  card,
  isLimitsEnabled,
  isTerminateEnabled,
}: {
  card: ApiCard | null | undefined;
  isLimitsEnabled: boolean;
  isTerminateEnabled: boolean;
}) {
  const { t } = useTranslation();

  if (!card) {
    return (
      <Repeat times={3}>
        <Skeleton variant="rounded" width={100} height={40} />
      </Repeat>
    );
  }

  switch (card.status) {
    case 'ACTIVE':
    case 'FROZEN':
      return (
        <>
          {isDetailsAvailable(card) ? <CardDetailsButton card={card} /> : null}
          {isFreezable(card) ? <CardFreezeButton card={card} /> : null}
          {isMoreAvailable(card) ? (
            <CardMoreButton
              card={card}
              isLimitsEnabled={isLimitsEnabled}
              isTerminateEnabled={isTerminateEnabled}
            />
          ) : null}
        </>
      );
    case 'ISSUED':
      return <ActivateCardButton card={card} />;
    case 'CREATED':
      return <CardAlert color="purple" icon={<TimeIcon />} title={t('Issuing your card…')} />;
    case 'ACTIVATING':
      return <CardAlert color="info" icon={<TimeIcon />} title={t('Activating your card…')} />;
    case 'TERMINATED':
      return (
        <CardAlert
          color="error"
          icon={<TerminateCardIcon />}
          title={t('Your card is terminated')}
        />
      );
    default:
      console.error('Unknown card status', card?.status);
      return null;
  }
}

export function CardActionsView({
  card,
  isAdmin,
}: {
  card: ApiCard | null | undefined;
  isAdmin: boolean;
}) {
  return (
    <Stack direction="row" justifyContent="center" gap={2}>
      <CardActions card={card} isLimitsEnabled={isAdmin} isTerminateEnabled={isAdmin} />
    </Stack>
  );
}
